import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { ListTodo, CheckCircle, Loader2, Clock, Search, LayoutGrid, List, Archive, ArchiveRestore, Eye, EyeOff, RefreshCw, AlertTriangle, Activity, Brain, ChevronDown, ChevronUp, Image as ImageIcon, Monitor } from "lucide-react";
import { NewAnalysisModal } from "@/components/NewAnalysisModal";
import { StatsCard } from "@/components/stats-card";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { tasksApi, type Task, type TaskStats, type DomainStats } from "@/api";
import { alibabaApi } from "@/api/alibaba";
import { useTaskSSE, type TaskProgress } from "@/hooks/useTaskSSE";

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  pending: { label: "待處理", variant: "outline" },
  processing: { label: "處理中", variant: "secondary" },
  completed: { label: "已完成", variant: "default" },
  failed: { label: "失敗", variant: "destructive" },
};

const formatDuration = (ms: number | undefined | null): string => {
  if (!ms || ms === 0) return "-";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

export default function Dashboard() {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewMode, setViewMode] = useState<"list" | "card">("card");
  const [showArchived, setShowArchived] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDomainPanel, setShowDomainPanel] = useState(false);
  const [expandedDomainRow, setExpandedDomainRow] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const progressMap = useTaskSSE();

  const { data: tasks = [], isLoading, error } = useQuery<Task[]>({
    queryKey: ["tasks", showArchived ? "archived" : "active"],
    queryFn: async () => {
      const res = await tasksApi.list({ limit: 200, archived: showArchived });
      return res.data || [];
    },
    refetchInterval: 5000,
  });

  const { data: stats } = useQuery<TaskStats>({
    queryKey: ["tasks-stats"],
    queryFn: async () => {
      const res = await tasksApi.stats();
      return res.data!;
    },
    refetchInterval: 5000,
  });

  const { data: domainStats = [] } = useQuery<DomainStats[]>({
    queryKey: ["domain-stats"],
    queryFn: async () => {
      const res = await tasksApi.domainStats();
      return res.data || [];
    },
    refetchInterval: 15000,
  });

  const { data: iframeCoverage } = useQuery({
    queryKey: ["iframe-coverage"],
    queryFn: async () => {
      const res = await alibabaApi.getIframeCoverage();
      return res.data;
    },
    refetchInterval: 30_000,
  });

  const { data: guiWorkers = [] } = useQuery({
    queryKey: ["gui-worker-status"],
    queryFn: async () => {
      const res = await alibabaApi.getGuiWorkerStatus();
      return res.data || [];
    },
    refetchInterval: 8_000,
  });

  const strategyLabels: Record<string, string> = {
    basic_ua_rotate: 'UA 輪換(P1)',
    cookie_farming: 'Cookie(P1)',
    tls_simulation: 'TLS(P1)',
    quickscraper_standard: 'QS 標準(P2)',
    quickscraper_domain_parser: 'QS 域解析(P2)',
    enhanced_headers: '強化標頭(P3)',
    mobile_ua: '行動UA(P3)',
    mobile_site: '行動站(P3)',
    quickscraper_premium: 'QS Premium+IP(P4)',
    quickscraper_json: 'QS JSON(P4)',
    flaresolverr: 'Flare CF(P5)',
    cf_bypass: 'CF Bypass(P5)',
    browserless_content: 'Browserless(P6)',
    browserless_unblock: 'Browserless+JS(P6)',
    browseract_tw: 'BA 真實瀏覽器(P7)',
    browseract_us: 'BA 真實瀏覽器US(P7)',
  };

  const PHASE_MAP: Record<string, number> = {
    basic_ua_rotate: 1, cookie_farming: 1, tls_simulation: 1,
    quickscraper_standard: 2, quickscraper_domain_parser: 2,
    enhanced_headers: 3, mobile_ua: 3, mobile_site: 3,
    quickscraper_premium: 4, quickscraper_json: 4,
    flaresolverr: 5, cf_bypass: 5,
    browserless_content: 6, browserless_unblock: 6,
    browseract_tw: 7, browseract_us: 7,
  };

  const displayStats = {
    total: stats?.total ?? tasks.length,
    completed: stats?.completed ?? 0,
    processing: (stats?.processing ?? 0) + (stats?.pending ?? 0),
    failed: stats?.failed ?? 0,
    avgTime: stats?.avg_duration_ms ? formatDuration(stats.avg_duration_ms) : "-",
  };

  const resetStuckMutation = useMutation({
    mutationFn: () => tasksApi.resetStuck(60),
    onSuccess: (res) => {
      const count = (res.data as { reset: number } | undefined)?.reset ?? 0;
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["tasks-stats"] });
      const processingCount = stats?.processing ?? 0;
      toast[count > 0 ? "success" : "info"](
        count > 0
          ? `已清除 ${count} 筆卡住超過 60 分鐘的任務`
          : processingCount > 0
            ? `佇列正常 — ${processingCount} 個任務排隊中（60 分鐘內有活動，非卡住）`
            : "佇列空閒，無卡住任務"
      );
    },
    onError: (err: Error) => toast.error(`清除失敗: ${err.message}`),
  });

  const unpauseDomainMutation = useMutation({
    mutationFn: (domain: string) => tasksApi.unpauseDomain(domain),
    onSuccess: (_res, domain) => {
      queryClient.invalidateQueries({ queryKey: ["domain-stats"] });
      toast.success(`已解除 ${domain} 的封鎖`);
    },
    onError: (err: Error) => toast.error(`解除失敗: ${err.message}`),
  });

  const clearAllPausesMutation = useMutation({
    mutationFn: () => tasksApi.clearAllDomainPauses(),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["domain-stats"] });
      toast.success(`已解除 ${res.data?.cleared ?? 0} 個網域的封鎖`);
    },
    onError: (err: Error) => toast.error(`解除失敗: ${err.message}`),
  });

  const smartRetryMutation = useMutation({
    mutationFn: () => tasksApi.smartRetryFailed(),
    onSuccess: (res) => {
      const data = res.data as { total: number; retried: number; downgraded: number } | undefined;
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["tasks-stats"] });
      if (data) {
        const parts: string[] = [`${data.retried}/${data.total} 個任務已重新排入佇列`];
        if (data.downgraded > 0) parts.push(`${data.downgraded} 個降為低優先`);
        toast.success(parts.join('，'));
      } else {
        toast.success("已重新排入佇列");
      }
    },
    onError: (err: Error) => toast.error(`重試失敗: ${err.message}`),
  });

  const archiveMutation = useMutation({
    mutationFn: (taskIds: string[]) => tasksApi.batchUpdate(taskIds, "archive"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      setSelectedTasks(new Set());
      toast.success("封存成功");
    },
    onError: (err: Error) => toast.error(`封存失敗: ${err.message}`),
  });

  const unarchiveMutation = useMutation({
    mutationFn: (taskIds: string[]) => tasksApi.batchUpdate(taskIds, "unarchive"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      setSelectedTasks(new Set());
      toast.success("取消封存成功");
    },
    onError: (err: Error) => toast.error(`取消封存失敗: ${err.message}`),
  });

  const handleSelectTask = (taskId: string, checked: boolean) => {
    const newSelected = new Set(selectedTasks);
    if (checked) newSelected.add(taskId); else newSelected.delete(taskId);
    setSelectedTasks(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedTasks(checked ? new Set(filteredTasks.map(t => t.task_id)) : new Set());
  };

  const filteredTasks = tasks.filter(t => {
    const ref = t.url || t.target_keyword || t.task_id || "";
    if (searchQuery && !ref.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (statusFilter !== "all" && t.status !== statusFilter) return false;
    return true;
  });

  // Live progress panel for processing tasks
  const activeTasks = Array.from(progressMap.values()).filter(
    p => p.status === 'processing' && p.step
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">SEO 分析任務總覽</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <StatsCard title="總任務數" value={displayStats.total} icon={ListTodo} color="info" testId="stats-total" />
        <StatsCard title="已完成" value={displayStats.completed} icon={CheckCircle} color="success" testId="stats-completed" />
        <StatsCard title="進行中" value={displayStats.processing} icon={Loader2} color="warning" testId="stats-active" />
        <StatsCard title="失敗" value={displayStats.failed} icon={AlertTriangle} color="destructive" testId="stats-failed" />
        <StatsCard title="平均耗時" value={displayStats.avgTime} icon={Clock} color="primary" testId="stats-duration" />
        {iframeCoverage && (
          <StatsCard
            title="iframe 覆蓋率"
            value={`${iframeCoverage.coverage_pct}%`}
            description={`${iframeCoverage.with_iframe_images} / ${iframeCoverage.total_alibaba_tasks} 任務有圖片`}
            icon={ImageIcon}
            color="info"
            testId="stats-iframe"
          />
        )}
      </div>

      {/* WIN GUI Worker Status */}
      {guiWorkers.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Monitor className="h-4 w-4 text-blue-500" />
              WIN GUI Worker 狀態 ({guiWorkers.filter(w => w.status === 'processing').length} 台處理中)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {guiWorkers.map(w => {
                let lastResult: Record<string, unknown> | null = null;
                try {
                  if (w.last_result) lastResult = JSON.parse(w.last_result);
                } catch { /* ignore */ }
                return (
                  <div key={w.device_name} className="p-2 rounded border bg-muted/30 space-y-1">
                    <div className="flex items-center gap-3">
                      <Badge
                        variant={w.status === 'processing' ? 'default' : 'outline'}
                        className={`shrink-0 text-xs ${w.status === 'processing' && w.task_type === 'fill_local' ? 'bg-purple-600' : ''}`}
                      >
                        {w.status === 'processing'
                          ? (w.task_type === 'fill_local' ? '📥 補齊中' : '🔍 爬取中')
                          : (w.task_type === 'fill_local' ? '📥 補齊完' : '⏸ 閒置')}
                      </Badge>
                      <span className="font-mono text-sm font-medium shrink-0">{w.device_name}</span>
                      {w.status === 'processing' && w.product_name && (
                        <span className="text-sm text-muted-foreground truncate" title={w.current_url ?? ''}>
                          {w.product_name}
                        </span>
                      )}
                      {w.current_url && (() => {
                        const m = w.current_url.match(/_(\d{10,})/);
                        return m ? (
                          <Link to={`/product/${m[1]}`} className="text-xs text-blue-400 hover:underline shrink-0 ml-1">
                            [查看]
                          </Link>
                        ) : null;
                      })()}
                      <span className="ml-auto text-xs text-muted-foreground shrink-0">
                        {new Date(w.updated_at).toLocaleTimeString('zh-TW')}
                      </span>
                    </div>
                    {/* Last result details — shown when idle after a fill */}
                    {w.status === 'idle' && lastResult && (
                      <div className="ml-1 pl-2 border-l-2 border-green-400 text-xs text-muted-foreground space-y-0.5">
                        {lastResult.product_name && (
                          <div><span className="text-foreground font-medium">商品名:</span> {String(lastResult.product_name).slice(0, 50)}</div>
                        )}
                        {lastResult.price && (
                          <div><span className="text-foreground font-medium">價格:</span> {String(lastResult.price)}</div>
                        )}
                        {lastResult.moq != null && (
                          <div><span className="text-foreground font-medium">MOQ:</span> {String(lastResult.moq)} 件</div>
                        )}
                        {lastResult.lead_time != null && (
                          <div><span className="text-foreground font-medium">貨期:</span> {String(lastResult.lead_time)} 天</div>
                        )}
                        {lastResult.iframe && (
                          <div className="truncate"><span className="text-foreground font-medium">iframe:</span> {String(lastResult.iframe).slice(0, 70)}</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Live scraping progress */}
      {activeTasks.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4 text-green-500 animate-pulse" />
              即時爬取進度 ({activeTasks.length} 個任務)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {activeTasks.map(tp => {
              const task = tasks.find(t => t.task_id === tp.task_id);
              const phaseColors = ['', 'text-blue-500', 'text-orange-500', 'text-purple-500', 'text-red-500'];
              const stepLabels: Record<string, string> = {
                fetching_html: '抓取 HTML',
                parsing_dom: '解析 DOM',
                extracting_assets: '提取資源',
                analyzing_seo: 'SEO 分析',
                saving_data: '儲存資料',
              };
              return (
                <div key={tp.task_id} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <Loader2 className="h-4 w-4 animate-spin text-blue-500 flex-shrink-0" />
                      <Link to={`/tasks/${tp.task_id}`} className="text-sm font-mono text-primary hover:underline truncate max-w-[300px]">
                        {task?.url || tp.task_id.slice(0, 12) + '...'}
                      </Link>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {(tp.priority === 'urgent' || task?.priority === 'urgent') && (
                        <Badge className="text-xs bg-orange-500 hover:bg-orange-500 text-white">⚡ 立刻處理</Badge>
                      )}
                      <Badge variant="secondary" className="text-xs">
                        {stepLabels[tp.step] || tp.step}
                      </Badge>
                    </div>
                  </div>

                  {/* Phase indicator */}
                  {tp.phase !== undefined && tp.phase > 0 && (
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5, 6].map(p => (
                          <div key={p} className={`h-2 w-8 rounded-full ${
                            p < tp.phase! ? 'bg-green-500' :
                            p === tp.phase! ? 'bg-blue-500 animate-pulse' :
                            'bg-muted'
                          }`} title={`Phase ${p}`} />
                        ))}
                      </div>
                      <span className={`text-xs font-medium ${phaseColors[tp.phase] || ''}`}>
                        P{tp.phase}/{tp.totalPhases ?? 6}
                      </span>
                      <span className="text-xs text-muted-foreground truncate">
                        {tp.phaseLabel}
                      </span>
                    </div>
                  )}

                  {/* Strategy & attempt info */}
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-muted-foreground">
                      {tp.strategyLabel && (
                        <span>策略: <span className="text-foreground font-medium">{tp.strategyLabel}</span></span>
                      )}
                    </div>
                    {tp.attempt && (
                      <span className="text-xs text-muted-foreground">
                        第 <span className="text-foreground font-medium">{tp.attempt}</span> 次嘗試
                      </span>
                    )}
                  </div>

                  {/* Progress bar */}
                  <Progress value={tp.progress} className="h-1.5" />
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Domain Intelligence Panel */}
      {domainStats.length > 0 && (
        <Card>
          <CardHeader className="pb-2 cursor-pointer" onClick={() => setShowDomainPanel(!showDomainPanel)}>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Brain className="h-4 w-4 text-purple-500" />
                網域爬取智慧 ({domainStats.length} 個網域)
              </CardTitle>
              {showDomainPanel ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </div>
            <CardDescription>
              依網域統計爬取策略成效，超過 10 筆且 3 次成功的策略會自動跳階加速
            </CardDescription>
          </CardHeader>
          {showDomainPanel && (
            <CardContent>
              {/* Legend + Clear All button */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><span className="text-red-500 font-medium">🚫</span> 暫停中：所有策略都試過後失敗，暫停 2 小時</span>
                  <span className="flex items-center gap-1"><span className="text-orange-500 font-medium">⏳</span> 待測：尚未成功</span>
                  <span className="flex items-center gap-1"><span className="text-green-600 font-medium">✅</span> 已成功</span>
                </div>
                {domainStats.some(d => d.is_blocklisted) && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs text-red-600 border-red-300 hover:bg-red-50"
                    onClick={() => clearAllPausesMutation.mutate()}
                    disabled={clearAllPausesMutation.isPending}
                  >
                    {clearAllPausesMutation.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                    解除全部封鎖 ({domainStats.filter(d => d.is_blocklisted).length})
                  </Button>
                )}
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-6"></TableHead>
                      <TableHead>網域</TableHead>
                      <TableHead className="text-center w-20">黑名單</TableHead>
                      <TableHead className="text-center">總/成/失</TableHead>
                      <TableHead className="text-center">成功率</TableHead>
                      <TableHead>最佳策略</TableHead>
                      <TableHead>上次爬取</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[...domainStats]
                      .sort((a, b) => {
                        // Sort order: (1) not-blocklisted 0% → most fixable first
                        //             (2) not-blocklisted >0% → already working
                        //             (3) blocklisted → hopeless
                        const aScore = a.is_blocklisted ? 2 : (a.success_rate === 0 ? 0 : 1);
                        const bScore = b.is_blocklisted ? 2 : (b.success_rate === 0 ? 0 : 1);
                        if (aScore !== bScore) return aScore - bScore;
                        return b.total_tasks - a.total_tasks;
                      })
                      .map(ds => {
                        const stratEntries = Object.entries(ds.strategies || {}).sort((a, b) => {
                          const phaseA = (PHASE_MAP as Record<string,number>)[a[0]] || 9;
                          const phaseB = (PHASE_MAP as Record<string,number>)[b[0]] || 9;
                          return phaseA - phaseB;
                        });
                        const isExpanded = expandedDomainRow === ds.domain;
                        return (
                          <>
                            <TableRow
                              key={ds.domain}
                              className="cursor-pointer hover:bg-muted/50"
                              onClick={() => setExpandedDomainRow(isExpanded ? null : ds.domain)}
                            >
                              <TableCell className="text-center">
                                {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                              </TableCell>
                              <TableCell className="font-mono text-xs max-w-[200px] truncate" title={ds.domain}>{ds.domain}</TableCell>
                              <TableCell className="text-center" onClick={e => e.stopPropagation()}>
                                {ds.is_blocklisted ? (
                                  <div className="flex flex-col items-center gap-1">
                                    <span className="text-xs font-medium text-red-600">🚫 暫停</span>
                                    {ds.paused_until && (
                                      <span className="text-[10px] text-muted-foreground">
                                        到 {new Date(ds.paused_until).toLocaleTimeString("zh-TW", { hour: "2-digit", minute: "2-digit" })}
                                      </span>
                                    )}
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-5 text-[10px] px-1.5 text-red-600 border-red-300 hover:bg-red-50"
                                      onClick={() => unpauseDomainMutation.mutate(ds.domain)}
                                      disabled={unpauseDomainMutation.isPending}
                                    >
                                      解除
                                    </Button>
                                  </div>
                                ) : ds.success_rate > 0 ? (
                                  <span className="text-xs font-medium text-green-600">✅ 正常</span>
                                ) : (
                                  <span className="text-xs font-medium text-orange-500">⏳ 待測</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center text-xs">
                                <span>{ds.total_tasks}</span>
                                <span className="text-green-600 mx-0.5">/{ds.completed}</span>
                                <span className="text-red-600">/{ds.failed}</span>
                              </TableCell>
                              <TableCell className="text-center">
                                <span className={`text-xs font-medium ${ds.success_rate >= 80 ? 'text-green-600' : ds.success_rate >= 20 ? 'text-yellow-600' : ds.success_rate > 0 ? 'text-orange-500' : 'text-red-500'}`}>
                                  {ds.success_rate}%
                                </span>
                              </TableCell>
                              <TableCell>
                                {ds.best_strategy ? (
                                  <div className="flex items-center gap-1">
                                    <Badge variant="outline" className="text-[10px] px-1">P{ds.best_phase}</Badge>
                                    <span className="text-xs">{strategyLabels[ds.best_strategy] || ds.best_strategy}</span>
                                  </div>
                                ) : (
                                  <span className="text-xs text-muted-foreground">未知</span>
                                )}
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                                {ds.last_scraped ? new Date(ds.last_scraped).toLocaleDateString("zh-TW") : "—"}
                              </TableCell>
                            </TableRow>
                            {/* Expanded: strategy breakdown */}
                            {isExpanded && (
                              <TableRow key={`${ds.domain}-detail`} className="bg-muted/10">
                                <TableCell colSpan={7} className="py-2 px-4">
                                  {stratEntries.length === 0 ? (
                                    <p className="text-xs text-muted-foreground">尚無策略嘗試紀錄（domain_strategy_cache 空）</p>
                                  ) : (
                                    <div className="space-y-1">
                                      <p className="text-[11px] font-semibold text-muted-foreground mb-1">策略嘗試紀錄：</p>
                                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1">
                                        {stratEntries.map(([name, stat]) => (
                                          <div key={name} className={`text-[10px] px-2 py-1 rounded border flex items-center justify-between gap-2 ${stat.success > 0 ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                                            <div>
                                              <div className="font-medium">{strategyLabels[name] || name}</div>
                                              <div className="text-muted-foreground">
                                                ✅{stat.success} ❌{stat.fail}
                                                {stat.avg_ms > 0 && <span className="ml-1">{(stat.avg_ms/1000).toFixed(1)}s</span>}
                                              </div>
                                            </div>
                                            {stat.success > 0 && <span className="text-green-600 font-bold">P{(PHASE_MAP as Record<string,number>)[name] || '?'}</span>}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        );
                      })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Reset Stuck button - always visible */}
      {displayStats.processing > 0 && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">有 {displayStats.processing} 個任務排隊/處理中</p>
                <p className="text-sm text-muted-foreground">
                  「Reset Stuck」清除 60 分鐘無活動的任務。若要強制清空全部，用「Force Reset All」。
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    if (confirm("確定要清除所有卡住的任務（processing > 1 小時）？")) {
                      resetStuckMutation.mutate();
                    }
                  }}
                  disabled={resetStuckMutation.isPending}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  {resetStuckMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 mr-2" />
                  )}
                  Reset Stuck
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    if (confirm(`強制重設全部 ${displayStats.processing} 個 processing 任務為 pending？\n⚠️ 正在執行中的任務也會被中斷。`)) {
                      tasksApi.resetStuck(1).then(res => {
                        const count = (res.data as { reset: number } | undefined)?.reset ?? 0;
                        queryClient.invalidateQueries({ queryKey: ["tasks"] });
                        queryClient.invalidateQueries({ queryKey: ["tasks-stats"] });
                        toast.success(`已強制重設 ${count} 個任務`);
                      }).catch((err: Error) => toast.error(`失敗: ${err.message}`));
                    }
                  }}
                  className="border-orange-300 text-orange-600 hover:bg-orange-50"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Force Reset All
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Smart retry button */}
      {displayStats.failed > 0 && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">有 {displayStats.failed} 個失敗任務</p>
                <p className="text-sm text-muted-foreground">
                  一鍵重試所有失敗任務。近 3 天內重試超過 3 次的會自動降為低優先排序。
                </p>
              </div>
              <Button
                variant="destructive"
                onClick={() => smartRetryMutation.mutate()}
                disabled={smartRetryMutation.isPending}
              >
                {smartRetryMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                一鍵重試失敗任務
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>任務列表</CardTitle>
              <CardDescription>最近的爬蟲分析任務</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {selectedTasks.size > 0 && (
                <>
                  <span className="text-sm text-muted-foreground">已選取 {selectedTasks.size} 個</span>
                  <Button variant="outline" size="sm" onClick={() => archiveMutation.mutate(Array.from(selectedTasks))} disabled={archiveMutation.isPending}>
                    <Archive className="h-4 w-4 mr-1" /> 封存
                  </Button>
                  {showArchived && (
                    <Button variant="outline" size="sm" onClick={() => unarchiveMutation.mutate(Array.from(selectedTasks))} disabled={unarchiveMutation.isPending}>
                      <ArchiveRestore className="h-4 w-4 mr-1" /> 取消封存
                    </Button>
                  )}
                </>
              )}
              <Button variant={showArchived ? "default" : "outline"} size="sm" onClick={() => setShowArchived(!showArchived)}>
                {showArchived ? <Eye className="h-4 w-4 mr-1" /> : <EyeOff className="h-4 w-4 mr-1" />}
                {showArchived ? "顯示全部" : "已封存"}
              </Button>
              <Button variant={viewMode === "card" ? "default" : "outline"} size="icon" onClick={() => setViewMode("card")}>
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button variant={viewMode === "list" ? "default" : "outline"} size="icon" onClick={() => setViewMode("list")}>
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-3 pt-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="搜尋 URL 或關鍵字..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部狀態</SelectItem>
                <SelectItem value="pending">待處理</SelectItem>
                <SelectItem value="processing">處理中</SelectItem>
                <SelectItem value="completed">已完成</SelectItem>
                <SelectItem value="failed">失敗</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center text-muted-foreground py-8">載入中...</div>
          ) : error ? (
            <div className="text-center text-destructive py-8">載入失敗，請重新整理頁面</div>
          ) : filteredTasks.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              <p className="mb-2">無符合條件的任務</p>
              <Button onClick={() => setIsModalOpen(true)}>新增分析</Button>
            </div>
          ) : viewMode === "list" ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox checked={selectedTasks.size === filteredTasks.length && filteredTasks.length > 0} onCheckedChange={handleSelectAll} />
                  </TableHead>
                  <TableHead>任務 ID</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead>關鍵字</TableHead>
                  <TableHead>狀態</TableHead>
                  <TableHead>建立時間</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTasks.map(task => {
                  const tp = progressMap.get(task.task_id);
                  const isActive = (task.status === 'processing' || task.status === 'pending');
                  return (
                  <TableRow key={task.task_id} className="cursor-pointer hover:bg-muted/50">
                    <TableCell>
                      <Checkbox checked={selectedTasks.has(task.task_id)} onCheckedChange={(checked) => handleSelectTask(task.task_id, checked as boolean)} onClick={(e) => e.stopPropagation()} />
                    </TableCell>
                    <TableCell>
                      <Link to={`/tasks/${task.task_id}`} className="text-primary hover:underline font-mono text-sm">{task.task_id.substring(0, 8)}...</Link>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">{task.url}</TableCell>
                    <TableCell><span className="text-sm">{task.target_keyword || "—"}</span></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant={statusConfig[task.status]?.variant}>{statusConfig[task.status]?.label || task.status}</Badge>
                        {isActive && tp?.phase !== undefined && tp.phase > 0 && (
                          <span className="text-xs text-muted-foreground">P{tp.phase}/{tp.totalPhases ?? 6} #{tp.attempt || 1}</span>
                        )}
                        {isActive && tp?.strategyLabel && (
                          <span className="text-xs text-muted-foreground truncate max-w-[120px]">{tp.strategyLabel}</span>
                        )}
                        {isActive && !tp && (
                          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                        )}
                        {task.status === 'completed' && task.success_strategy && (
                          <span className="text-xs text-muted-foreground">P{task.success_phase} {strategyLabels[task.success_strategy] || task.success_strategy}</span>
                        )}
                        {task.status === 'failed' && task.retry_count !== undefined && task.retry_count > 0 && (
                          <span className="text-xs text-destructive">失敗{task.retry_count}次</span>
                        )}
                      </div>
                      {isActive && tp?.progress !== undefined && (
                        <Progress value={tp.progress} className="h-1 mt-1" />
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">{new Date(task.created_at).toLocaleString("zh-TW")}</TableCell>
                    <TableCell>
                      <Link to={`/tasks/${task.task_id}`}><Button variant="ghost" size="sm">查看</Button></Link>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredTasks.map(task => {
                const tp = progressMap.get(task.task_id);
                const isActive = (task.status === 'processing' || task.status === 'pending');
                const stepLabels: Record<string, string> = {
                  fetching_html: '抓取 HTML',
                  parsing_dom: '解析 DOM',
                  extracting_assets: '提取資源',
                  analyzing_seo: 'SEO 分析',
                  saving_data: '儲存資料',
                };
                return (
                <div key={task.task_id} className="relative">
                  <div className="absolute top-3 left-3 z-10">
                    <Checkbox checked={selectedTasks.has(task.task_id)} onCheckedChange={(checked) => handleSelectTask(task.task_id, checked as boolean)} />
                  </div>
                  {task.archived && (
                    <div className="absolute top-3 right-3 z-10">
                      <Badge variant="secondary" className="text-xs"><Archive className="h-3 w-3 mr-1" /> 已封存</Badge>
                    </div>
                  )}
                  <Link to={`/tasks/${task.task_id}`}>
                    <Card className="hover:bg-muted/50 transition-colors cursor-pointer">
                      <CardContent className="p-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge variant={statusConfig[task.status]?.variant}>{statusConfig[task.status]?.label || task.status}</Badge>
                            {isActive && tp?.step && (
                              <span className="text-xs text-muted-foreground">{stepLabels[tp.step] || tp.step}</span>
                            )}
                          </div>
                          {task.duration_ms && <span className="text-sm text-muted-foreground">{formatDuration(task.duration_ms)}</span>}
                        </div>
                        <p className="font-mono text-sm text-primary truncate">{task.url}</p>
                        {task.target_keyword && <p className="text-sm text-muted-foreground truncate">關鍵字: {task.target_keyword}</p>}

                        {/* Inline progress for processing/pending tasks */}
                        {isActive && tp && tp.phase !== undefined && tp.phase > 0 && (
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-2">
                              <div className="flex gap-0.5">
                                {[1, 2, 3, 4, 5, 6].map(p => (
                                  <div key={p} className={`h-1.5 w-5 rounded-full ${
                                    p < tp.phase! ? 'bg-green-500' :
                                    p === tp.phase! ? 'bg-blue-500 animate-pulse' :
                                    'bg-muted'
                                  }`} />
                                ))}
                              </div>
                              <span className="text-xs text-muted-foreground">P{tp.phase}/{tp.totalPhases ?? 6}</span>
                              {tp.attempt && <span className="text-xs text-muted-foreground">#{tp.attempt}</span>}
                            </div>
                            {tp.strategyLabel && (
                              <p className="text-xs text-muted-foreground truncate">{tp.strategyLabel}</p>
                            )}
                            <Progress value={tp.progress} className="h-1" />
                          </div>
                        )}
                        {isActive && !tp && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            <span>{task.status === 'pending' ? '等待排入佇列...' : '等待進度更新...'}</span>
                          </div>
                        )}

                        {/* Success strategy & retry info */}
                        {task.status === 'completed' && task.success_strategy && (
                          <div className="flex items-center gap-1 text-xs">
                            <Badge variant="outline" className="text-xs">P{task.success_phase}</Badge>
                            <span className="text-muted-foreground">{strategyLabels[task.success_strategy] || task.success_strategy}</span>
                          </div>
                        )}
                        {task.status === 'failed' && task.retry_count !== undefined && task.retry_count > 0 && (
                          <div className="text-xs text-destructive">
                            失敗 {task.retry_count} 次
                          </div>
                        )}

                        <div className="flex items-center justify-between text-sm text-muted-foreground">
                          <span>{task.domain || ""}</span>
                          <span>{new Date(task.created_at).toLocaleString("zh-TW", { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <NewAnalysisModal open={isModalOpen} onOpenChange={setIsModalOpen} />
    </div>
  );
}
